import { createSelector } from 'reselect';
import { RootState } from '../../../types';
import { EntityTypes } from '../../../constants/entities';
import { IEntityReducer } from '../../reducers/entities';
import { IModule, IModuleAssign, IModuleDetailDAO, IModuleVersion, ModuleStage } from 'types/domain/module';
import { getEntities } from '../utils/getEntities';

import { ITagJoin } from 'types/domain/tag';
import { parseVariableDAO } from '../admin/variable';
import { selectSessionMode } from '../admin/sessionMode';
import { Mode } from 'core/slices/session';
import { IPreviewState } from 'core/slices/preview';
import { createCachedSelector } from 're-reselect';
import { ICourseNode } from 'types/domain/course';

const entity = (state: RootState) => state.entity.present;
const id = (state: RootState, id?: number) => (id ? id : state.state[EntityTypes.MODULE]);
const mode = selectSessionMode;
const preview = (state: RootState) => state.preview;

export const selectModuleCached = createCachedSelector(
  entity,
  id,
  parseModuleDAO,
)((state, id) => id || state.state[EntityTypes.MODULE] || 'unknown');
export const selectModule = createSelector([entity, id], parseModule);
export const selectModuleDAO = createSelector([entity, id], parseModuleDAO);
export const selectModuleVariable = createSelector([entity, id], parseModuleVariable);
export const selectModuleActionsLength = createSelector([entity, id], parseModuleActionsLength);
export const selectModuleAssign = createSelector([entity, id, mode, preview], parseModuleAssign);
export const selectModuleQuestionCount = createSelector([entity, id], parseModuleQuestionCount);

/**
 * Questions in a loaded module: slides with answers to choose between. A
 * content slide has none, and counting the answers themselves would count each
 * response rather than the question — the same line LearnerModules draws.
 */
export function parseModuleQuestionCount(entity: IEntityReducer, id: number) {
  const answerable = new Set<number>(
    getEntities(entity, EntityTypes.ACTION_ENTITY).map((actionEntity: { actionId?: number }) => actionEntity.actionId),
  );

  return getEntities(entity, EntityTypes.ACTION, 'moduleId', id).filter((action: { id: number }) =>
    answerable.has(action.id),
  ).length;
}

/**
 * Whether the store already holds a complete, up-to-date copy of the module — the same
 * definition of "fresh" the loadModule saga uses to decide against re-requesting it.
 * Switching between edit and preview re-mounts the page, and without this both sides
 * re-fetch a module the store just deep-loaded.
 */
export const selectModuleFresh = createSelector([entity, id], parseModuleFresh);

export function parseModuleFresh(entity: IEntityReducer, id: number) {
  const module: IModule = entity[EntityTypes.MODULE][id];

  return !!module && module.__stage === ModuleStage.DEEP && !module.__stale;
}

export function parseModule(entity: IEntityReducer, id: number) {
  const module: IModule = entity[EntityTypes.MODULE][id];
  const text = module && module.textId ? entity[EntityTypes.TEXT][module.textId] : null;
  return module
    ? ({
        ...module,
        name: text ? text.shortText || module.name : module.name,
        image: module.imageId ? entity[EntityTypes.IMAGE][module.imageId] : null,
        text,
        variable: parseVariableDAO(entity, module.variableId),
        versions: getEntities(entity, EntityTypes.MODULE_VERSION, 'moduleId', id) as IModuleVersion[],
        tags: getEntities(entity, EntityTypes.TAG_JOIN, 'moduleId', id) as ITagJoin[],
      } as IModule)
    : null;
}

export function parseModuleVariable(entity: IEntityReducer, id: number) {
  const module: IModule = entity[EntityTypes.MODULE][id];

  return module ? parseVariableDAO(entity, module.variableId) : null;
}

export function parseModuleActionsLength(entity: IEntityReducer, id: number) {
  const module: IModule = entity[EntityTypes.MODULE][id];

  if (!module) return 0;

  return moduleStepCount(module, module.actions ?? getEntities(entity, EntityTypes.ACTION, 'moduleId', id));
}

/**
 * Slides in a module, from whichever of the two the payload gave us.
 *
 * A module the store has deep-loaded (`get MOD`) has its actions in the ACTION bucket,
 * and that count is the live one — it moves as the author adds and deletes slides. A
 * module that only arrived as part of a course or a list has none: those payloads carry
 * `actionCount` instead of the slides themselves, which is what keeps `get COU` from
 * shipping every slide's text to render a number on a tile.
 *
 * Loaded actions win only once the module is deep-loaded, so an author editing a module
 * sees their own edits rather than the count frozen at whatever the course load said.
 * Before that the ACTION bucket can hold a stray slide or two — the tile preview's first
 * slide, a learner's progress — and counting those read "1 step" on every course tile.
 */
export function moduleStepCount(module: Pick<IModule, 'actionCount' | '__stage'> | null, actions?: unknown[]) {
  if (module?.__stage === ModuleStage.DEEP || module?.actionCount == null) return actions?.length ?? 0;

  return module.actionCount;
}

export function parseModuleDAO(entity: IEntityReducer, id: number) {
  const module: IModule = entity[EntityTypes.MODULE][id];
  const actions = getEntities(entity, EntityTypes.ACTION, 'moduleId', id);
  const outs = getEntities(entity, EntityTypes.MODULE_OUT, 'moduleId', id);
  const text = module && module.textId ? entity[EntityTypes.TEXT][module.textId] : null;
  const courseNodes: ICourseNode[] = getEntities(entity, EntityTypes.COURSE_NODE, 'moduleId', id);

  return module
    ? ({
        ...module,
        name: text ? text.shortText : module.name,
        image: module.imageId ? entity[EntityTypes.IMAGE][module.imageId] : null,
        text,
        variable: parseVariableDAO(entity, module.variableId),
        outs,
        actions,
        courseNodes,
        courseName: courseNodes.length ? entity[EntityTypes.COURSE][courseNodes[0].courseId]?.name ?? null : null,
        //versions: getEntities(entity, EntityTypes.MODULE_VERSION, 'moduleId', id) as IModuleVersion[],
        //tags: getEntities(entity, EntityTypes.TAG_JOIN, 'moduleId', id) as ITagJoin[],
      } as IModuleDetailDAO)
    : null;
}

export function parseModuleAssign(entity: IEntityReducer, id: number, mode: Mode, preview: IPreviewState) {
  const module: IModuleAssign = entity[EntityTypes.MODULE][id] as IModuleAssign;

  return module
    ? ({
        ...module,
        sessionAttemptId:
          mode === Mode.PREVIEW
            ? preview.modules[id]
              ? preview.modules[id].sessionAttemptId
              : null
            : module.sessionAttemptId,
        sessionAttempt: mode === Mode.LEARNING ? entity[EntityTypes.SESSION_ATTEMPT][module.sessionAttemptId] : null,
      } as IModuleAssign)
    : null;
}

export const selectModuleTextId = createSelector(
  entity,
  id,
  (entity, id) => entity[EntityTypes.TEXT][entity[EntityTypes.MODULE][id]?.textId]?.id,
);

export const selectModuleType = createSelector(entity, id, (entity, id) => entity[EntityTypes.MODULE][id]?.type);
